/**
 * Tenant Settings — AI-team skills/MCP notice (read-only).
 * Not hardware Skills (/skills/library). Writes stay on /sa/ai-tools.
 */
import { useMemo } from "react";
import { html, Ico, Card, Alert, KV } from "./ui.js";
import { path, useApi } from "./api.js";
import { autonomyPlanesOf, fourVisiblesOf } from "./autonomy-planes.js";
import { operatorSay, whySay } from "./operator-say.js";

export function teamToolNoticeOf(skillsPack, mcpPack) {
  const skills = (skillsPack && (skillsPack.items || skillsPack.skills)) || [];
  const servers = (mcpPack && (mcpPack.items || mcpPack.servers)) || [];
  const skillRows = Array.isArray(skills) ? skills : [];
  const mcpRows = Array.isArray(servers) ? servers : [];
  const newSkills = skillRows.filter((s) => s && s.new === true);
  const newMcp = mcpRows.filter((s) => s && s.new === true);
  return {
    skillsN: skillRows.length,
    mcpN: mcpRows.length,
    newN: newSkills.length + newMcp.length,
    names: [
      ...newSkills.map((s) => String(s.name || s.skill_id || "").trim()).filter(Boolean),
      ...mcpRows.filter((s) => s && s.new === true).map((s) => String(s.name || "").trim()).filter(Boolean),
    ].slice(0, 8),
    write: skillsPack && skillsPack.write === true,
    audience: String((skillsPack && skillsPack.audience) || "tenant_notice"),
    newOnly: skillsPack && skillsPack.new_only === true,
    hardware: false,
  };
}

export function AiTeamToolsNotice({ toast }) {
  const skills = useApi(path("get__ai_platform_skills"));
  const mcp = useApi(path("get__ai_platform_mcp-servers"));
  const home = useApi("/ai/home-context");
  const four = fourVisiblesOf(
    autonomyPlanesOf(home.data && home.data.autonomy),
    { loading: !!(home.loading && !home.data) },
  );
  const note = useMemo(
    () => teamToolNoticeOf(skills.data, mcp.data),
    [skills.data, mcp.data],
  );
  const loading = !!(skills.loading && !skills.data) || !!(mcp.loading && !mcp.data);
  const blocked =
    /\b403\b|forbidden|seat_gated/i.test(String(skills.error || ""))
    || /\b403\b|forbidden|seat_gated/i.test(String(mcp.error || ""));
  const unarmed = /\b404\b|ai_platform_disabled/i.test(String(skills.error || mcp.error || ""));

  const reload = () => {
    skills.reload && skills.reload();
    mcp.reload && mcp.reload();
    home.reload && home.reload();
    toast && toast("AI team tools refreshed", "go");
  };

  return html`<div className="ai-set-desk ai-set-desk--liquid"
      data-cmc-audience="tenant-ai-tools-notice"
      data-cmc-ai-team-tools="notice">
    <div className="ai-set-desk__rail">
      <div>
        <p className="eyebrow">Settings · AI team tools</p>
        <h2 className="ai-set-desk__title">Catalogue for the AI team</h2>
      </div>
      <button type="button" className="btn btn--ghost btn--sm" disabled=${loading}
        onClick=${reload}><${Ico} d="refresh" w=${13}/> Refresh</button>
    </div>
    <div className="ops-cockpit__metrics" data-cmc-four-visibles="1" data-cmc-audience="tenant-plus-global-rails">
      <div className="ops-cockpit__m" data-cmc-desired-effective="desired vs effective"><b>${operatorSay(four.desired, four.desired || "—")}</b><span>Wanted / live</span></div>
      <div className="ops-cockpit__m"><b>${four.rev}</b><span>Rule version</span></div>
      <div className="ops-cockpit__m" data-cmc-ack-lag="ACK / lag"><b>${four.ack}</b><span>Boards answered</span></div>
      <div className="ops-cockpit__m"><b>${whySay(four.why)}</b><span>Why blocked</span></div>
    </div>
    ${unarmed ? html`<${Alert} tone="info" title="Not armed"
      say="AI team tools are off on this deployment."/>` : null}
    ${blocked ? html`<${Alert} tone="warn" title="Sealed"
      say="This seat cannot read the AI team catalogue."/>` : null}
    <${Card} title=${note.newOnly ? "New this fortnight" : "Live catalogue"}
        meta=${loading ? "…" : (note.write ? "Staff can edit" : "Read only")}
        sub="Not board / hardware Skills.">
      ${note.newOnly ? null : html`<${KV} k="Skills" v=${loading ? "…" : String(note.skillsN)}/>`}
      ${note.newOnly ? null : html`<${KV} k="Tools" v=${loading ? "…" : String(note.mcpN)}/>`}
      <${KV} k="New" v=${loading ? "…" : String(note.newN)}/>
      ${note.names.length
        ? html`<p className="note">${note.names.join(" · ")}</p>`
        : html`<p className="note">${loading ? "Reading…" : "No new tools this fortnight."}</p>`}
    <//>
  </div>`;
}

export default AiTeamToolsNotice;
